import React, {useRef, useState} from "react";
import { OctagonAlert, SquarePlus, Trash2, XIcon } from "lucide-react";
import {IFileUploader, IFileUploaderItem} from "types/gaiaComponents/fileUploader";
import { ICON_SIZE, KEY } from "components/common/constants";

export const FileUploaderType = {
    DEFAULT: "default",
    DRAG_AND_DROP: "drag_and_drop",
}

export const FileUploaderState = {
    ENABLED: "enabled",
    DISABLED: "disabled",
}

const FileUploaderItem: React.FC<IFileUploaderItem> = ({ fileName, icon, onRemove, tabIndex }) => {
    return (
        <>
            <span>{fileName}</span>
            <span
                role="button"
                tabIndex={tabIndex}
                className="ga-file_uploader-item_file__icon"
                onClick={onRemove}
                aria-label="Remove file"
                onKeyDown={e => {
                    if (e.key === KEY.ENTER || e.key === KEY.SPACE) {
                        e.preventDefault();
                        onRemove();
                    }
                }}
            >
                {icon}
            </span>
        </>
    );
};




export const FileUploader : React.FC<IFileUploader> = (props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [internalError, setInternalError] = useState<string | null>(null);
    const [internalFile, setInternalFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const clearFileState = () => {
        setInternalError(null);
        setInternalFile(null);
        props.onFileSelect(null);
    };

    const processFile = (file: File | null) => {
        if (!file) {
            clearFileState();
            return;
        }

        // Store the file in internal state
        setInternalFile(file);

        // Validate file size if maxFileSize is provided
        if (props.maxFileSize && file.size > props.maxFileSize) {
            setInternalError(props.translate("fileUploader.tooLargeFile"));
            props.onFileSelect(null);
            return;
        }

        // Clear internal error and call onFileSelect only if validation passes
        setInternalError(null);
        props.onFileSelect(file);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        resetFileInput();
        processFile(file);
    };

    const handleRemoveFile = () => {
        clearFileState();
        resetFileInput();
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    };

    const renderUploadButton = () => {
        switch (props.type) {
            case FileUploaderType.DRAG_AND_DROP:
                return (
                    <button
                        className={`ga-file_uploader--drag_and_drop__box ${isDragging ? 'ga-file_uploader--drag_and_drop__box--drag-hover' : ''}`}
                        onClick={handleUploadClick}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        tabIndex={props.tabIndex}
                        type="button"
                        disabled={props.state === FileUploaderState.DISABLED}
                    >
                        <p>{props.translate("fileUploader.dropFiles")}</p>
                    </button>
                );
            case FileUploaderType.DEFAULT:
            default:
                return (
                    <button
                        className="ga-button ga-button--primary"
                        onClick={handleUploadClick}
                        tabIndex={props.tabIndex}
                        type="button"
                        disabled={props.state === FileUploaderState.DISABLED}
                    >
                        <SquarePlus className="ga-icon" /> {props.translate("fileUploader.uploadFiles")}
                    </button>
                );
        }
    };

    const renderFileItem = (displayFile: File, displayError: string | null) => {
        const tabIndex= (props.tabIndex ?? 0) + 1;
        if (!displayError) {
            return (
                <div className="ga-file_uploader-item_file ga-file_uploader-item_file--uploaded">
                    <FileUploaderItem
                        fileName={displayFile.name}
                        icon={<XIcon size={ICON_SIZE.SMALL} />}
                        onRemove={handleRemoveFile}
                        tabIndex={tabIndex}
                    />
                </div>
            );
        }

        return (
            <div className="ga-file_uploader-item_file ga-file_uploader-item_file--error">
                <div className="ga-file_uploader-item_file__error_file">
                    <FileUploaderItem
                        fileName={`${displayFile.name} (${props.translate("fileUploader.error")})`}
                        icon={<Trash2 size={ICON_SIZE.SMALL} />}
                        onRemove={handleRemoveFile}
                        tabIndex={tabIndex}
                    />
                </div>
                <div className="ga-file_uploader-item_file__error_callout">
                    <OctagonAlert className="ga-file_uploader-item_file__error_callout__icon" />
                    <span>{displayError}</span>
                </div>
            </div>
        );
    };

    // Use internal error if present, otherwise fall back to external error prop
    const displayError = internalError || props.error;
    // Use internal file state as the source of truth for display
    const displayFile = internalFile;

    const showFileList = displayFile && props.state !== FileUploaderState.DISABLED;

    return (
        <div className={`ga-file_uploader ga-file_uploader--${props.type} ga-file_uploader--${props.state}`}>
            <div className="ga-file_uploader__heading">
                <span className="ga-file_uploader__heading__label">{props.header.label}</span>
                <span className="ga-file_uploader__heading__description">{props.header.description}</span>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                accept={props.acceptedFileExtensions}
                onChange={handleFileChange}
                disabled={props.state === FileUploaderState.DISABLED}
            />

            {renderUploadButton()}

            {showFileList && (
                <div className="ga-file_uploader-file_list-item">
                    {renderFileItem(displayFile, displayError)}
                </div>
            )}
        </div>
    );
}